import { AlertCircle, ExternalLink, X, Info } from 'lucide-react';

/**
 * DictationError — inline banner that surfaces dictation failures.
 *
 * Special handling for iframe blocking: shows a prominent "Open in new tab"
 * button because that's the only real recovery path (browsers refuse mic
 * access in iframes without `allow="microphone"`, which Emergent's preview
 * pane does not pass).
 */
export default function DictationError({ error, inIframe, onDismiss }) {
  if (!error) return null;

  const isIframeBlock = inIframe && /preview frame|Microphone is blocked/i.test(error);
  const newTabUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (isIframeBlock) {
    return (
      <div
        className="mb-3 rounded-2xl bg-amber-50 border border-amber-300 p-4"
        data-testid="dictation-error-banner"
        role="alert"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900 mb-1">
              Microphone is blocked in this preview
            </div>
            <p className="text-sm text-amber-800 leading-relaxed mb-3">
              Browsers don't allow mic access inside the Emergent preview iframe.
              This is a platform limitation, not a bug in PlayFirst — your real
              users will not see this. Open the app in a new tab to dictate.
            </p>
            <a
              href={newTabUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition-colors"
              data-testid="dictation-open-newtab"
            >
              Open in new tab to dictate <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="text-amber-700 hover:text-amber-900 flex-shrink-0"
              data-testid="dictation-error-dismiss"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Generic dictation error
  return (
    <div
      className="mb-3 rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5"
      data-testid="dictation-error-banner"
      role="alert"
    >
      <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm text-amber-900 leading-relaxed">
        <span className="font-medium">Dictation:</span> {error}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-amber-700 hover:text-amber-900 flex-shrink-0"
          data-testid="dictation-error-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * IframeDictationNotice — proactive notice shown *before* a user clicks
 * Dictate when running inside an iframe context (e.g., Emergent preview).
 * Avoids the click → error → recover dance.
 */
export function IframeDictationNotice({ inIframe }) {
  if (!inIframe) return null;
  const newTabUrl = typeof window !== 'undefined' ? window.location.href : '';
  return (
    <div
      className="mb-3 rounded-xl bg-blue-50 border border-blue-200 p-3 flex items-start gap-2.5 text-sm"
      data-testid="iframe-dictation-notice"
    >
      <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-blue-900 leading-relaxed">
        <span className="font-medium">Heads up:</span> dictation needs mic access,
        which is blocked inside this preview pane.{' '}
        <a
          href={newTabUrl}
          target="_blank"
          rel="noreferrer"
          className="text-blue-900 underline hover:no-underline font-medium inline-flex items-center gap-1"
          data-testid="iframe-notice-open-newtab"
        >
          Open in new tab <ExternalLink className="w-3 h-3" />
        </a>{' '}
        to use it. (Real users won't see this notice.)
      </div>
    </div>
  );
}
